<template>
<!-- eslint-disable max-len -->
<span>
  <transition enter-active-class="animated slideInDown" leave-active-class="animated fadeOut">
    <div class="report-container" id="report-container">
      <div :class="`report ${pdf ? 'pdf' : ''}`" ref="report" id="report">
        <div :class="`report-header ${pdf ? 'pdf' : ''}`">
          <!-- logo -->
          <img :src="resolveAssetPath(activeEnvironment.reportLogo || activeEnvironment.creditsLogo || activeEnvironment.titleLogo, 'ui/customer')" :class="`logo ${pdf ? 'pdf' : ''}`" ref="logo">
          <!-- title -->
          <h1 class="report-title no-wrap">{{activeEnvironment.reportTitle || game.reportTitle || 'Global Leadership Framework Overview'}}</h1>
        </div>

        <!-- name/date -->
        <div :style="{
          display: 'flex',
          'flex-flow': 'row nowrap',
          'justify-content': 'space-between',
          padding: '4px 50px',
          'font-size': '1.35em',
        }">
          <div class="no-wrap">
            {{username}}
          </div>
          <div class="no-wrap">
            {{date}}
          </div>
        </div>

        <!-- green divider -->
        <div :style="{
          'background-color': 'rgba(134,188,37,1)',
          height: '3px',
        }">
        </div>

        <!-- thank you blurb -->
        <div :style="{
          padding: '8px 50px',
          'font-size': '1em',
        }">
          Thank you for completing the global leadership framework gamulation!
          Your personal report is based on your demonstrated decisions and preferences.
          What you've demonstrated expresses what comes most naturally to you at this time,
          which can give you clues into the types of roles, positions, and experiences that
          will often feel most aligned and empowering to you.
          There are no good, no bad, no better or no worse abilities.
          All humans can, and do, express every capability at different times and you can also develop all your capabilities.
          Below you can learn more about the capabilities that you most uniquely demonstrate in a work context.
        </div>

        <!-- banner -->
        <div class="middle-banner"
          :style="{
            display: 'flex',
            'flex-flow': 'row nowrap',
            margin: '5px 0 2px',
            width: '100%',
            height: '145px',
          }"
        >
          <!-- banner text -->
          <div
            :style="{
              margin: '20px 0 0 125px',
            'font-size': '24px',
            }"
          >Go as far as your ambition takes you</div>
        </div>

        <!-- tabs -->
        <div v-if="!pdf" class="archetype-tabs">
          <!-- white line -->
          <div
            :style="{
              position: 'absolute',
              top: '50%',
              left: '0',
              width: '100%',
              height: '1px',
              'background-color': 'white',
            }"
          >
          </div>
          <!-- tab -->
          <div v-for="(name, i) in sortedTopArchetypeNames"
            :key="i"
            class="archetype-tab"
            :class="{ selected: (selectedTab === i) }"
            type="button"
            @click.prevent="() => onClickTab(i)"
          >
            {{name}}
          </div>
        </div>

        <!-- archetype header -->
        <div :class="`archetype-header no-wrap`">
          <div :style="{
            margin: '10px 50px 10px 375px',
            padding: '0',
          }">
            {{(sortedTopArchetypeNames && sortedTopArchetypeNames.length > selectedTab) ? sortedTopArchetypeNames[selectedTab] : 'THE CURIOUS'}}
          </div>

          <!-- selected archetype icon -->
          <div :style="{
            position: 'absolute',
            top: '0',
            left: '45px',
            width: '285px',
            height: '285px',
            'z-index': '10',
          }">
            <img class="selected-icon"
              :src="`/static/reports/deloittearchetypes/${
                selectedArchetypeIcon || 'Icon_Curious'
              }.png`"
            />
          </div>

          <!-- vertical divider -->
          <div :style="{
            position: 'absolute',
            top: '25px',
            left: '340px',
            width: '20px',
            height: '170px',
            'background-color': 'rgba(134,188,37,.9)',
          }">
          </div>
        </div>

        <!-- archetype summary -->
        <div :style="{
          margin: '10px 50px 10px 375px',
          'font-size': '27px',
          'line-height': '125%',
          'min-height': '120px',
          padding: '0',
        }">
          {{selectedArchetypeSummary}}
        </div>

        <!-- green divider -->
        <div :style="{
          margin: '12px 0 4px',
          padding: '3px 100px',
          'font-size': '19px',
          'background-color': 'rgba(134,188,37,.9)',
          color: 'white',
          position: 'relative',
          'z-index': '1',
          height: '50px',
        }"></div>

        <!-- behaviors header -->
        <div :style="{
          margin: '12px 0 4px',
          padding: '3px 100px',
          'font-size': '19px',
          'background-color': 'white',
          color: 'black',
          position: 'relative',
          'z-index': '1',
        }">
          How might this show up at work?<br>Behaviors that typically drive and fuel this archetype are:
        </div>

        <!-- behaviors -->
        <div :style="{
          margin: '6px 0 4px 815px',
          padding: '0 100px',
          'font-size': '10px',
        }">
          LEVEL
        </div>
        <div
          v-for="(b, i) in selectedArchetypeBehaviors"
          :key="i"
          :style="{
            display: 'flex',
            'flex-flow': 'column nowrap',
            'align-items': 'flex-start',
            'justify-content': 'center',
            padding: '0 100px',
            margin: '0',
            width: '100%',
            'background-color': 'white',
          }"
        >
          <div
            :style="{
              display: 'flex',
              'flex-flow': 'row nowrap',
              'align-items': 'center',
              'justify-content': 'flex-start',
              padding: '0',
              margin: '0 0 20px',
              height: '45px',
              'background-color': 'white',
            }"
          >
            <div class="behavior-bullet">
              <img class="behavior-bullet-icon"
                :src="`/static/reports/archetypes/${
                  selectedArchetypeIcon || 'Icon_Curious'
                }.png`"
              />
            </div>
            <div :style="{
              width: '650px',
              margin: '0 45px 0 25px',
              'line-height': '125%',
            }">
              {{b.text}}
            </div>
            <star-box :stars="b.level" green></star-box>
          </div>
          <!-- green divider -->
          <div
            v-if="i < selectedArchetypeBehaviors.length - 1"
            :style="{
              position: 'relative',
              top: '-10px',
              width: '100%',
              height: '1px',
              'background-color': 'rgba(134,188,37,1)',
            }"
          >
          </div>
        </div>

        <!-- green divider -->
        <div :style="{
          'background-color': 'rgba(134,188,37,1)',
          height: '15px',
          margin: '20px 0',
        }">
        </div>

        <!-- links banner background -->
        <div
          class="links-banner"
          :style="{
            display: 'flex',
            'flex-flow': 'row nowrap',
            'align-items': 'flex-end',
            'justify-content': 'flex-start',
            margin: '0',
            padding: '0 75px',
            height: '205px',
          }"
        >
          <!-- inexplicable top margin needed for iOS because everything is always broken in Safari -->
          <div
            v-for="(link, i) in readMoreLinks"
            :key="i"
            :style="{
              display: 'flex',
              'flex-flow': 'column nowrap',
              'align-items': 'center',
              'justify-content': 'center',
              margin: 'auto 0 0',
              height: '45%',
              width: '16.67%',
              'text-align': 'center',
              'font-size': '85%',
            }"
          >
            <!-- text -->
            <div
              :style="{
                height: '48%',
                'margin-bottom': '2%',
              }"
            >{{link.text}}</div>
            <!-- read more button -->
            <a
              :style="{
                height: '40%',
                width: '90%',
                margin: '0 5%',
                border: '2px solid rgba(134,188,37,1)',
                'border-radius': '5px',
                color: 'white',
                display: 'flex',
                'flex-flow': 'row nowrap',
                'align-items': 'center',
                'justify-content': 'center',
              }"
              target="_blank"
              :href="link.link"
            >
              <!-- inexplicable margin needed for iOS because everything is always broken in Safari -->
              <div class="no-wrap" :style="{ margin: 'auto 0' }">{{ link.verb }} MORE</div>
            </a>
          </div>
        </div>

        <!-- footer message (e.g. displaying ©) -->
        <div :style="{
          padding: '2px 25px',
          width: '100%',
          'border-top': '1px solid white',
          'background-color': 'black',
          color: 'white',
        }">
          {{activeEnvironment.reportFooter}}
        </div>
      </div>
    </div>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <div v-if="showDownloading" class="download-message">Downloading!</div>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <button v-if="showNextButton"
      class="btn download-button"
      @click.prevent="onClickDownload"
    >Download</button>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <bottom-bar v-if="showNextButton" :next="showNarratorNotification ? onComplete : undefined" :tabindex="3" ref="bottomBar" :noBar="true">
      <span slot="button">
        <span v-html="(narrative && narrative.nextButtonText) ? narrative.nextButtonText : ((game && game.nextButtonDefaultText) ? game.nextButtonDefaultText : 'Next')"></span>
      </span>
    </bottom-bar>
  </transition>
</span>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import JsPDF from 'jspdf';
import domtoimage from 'dom-to-image-more';

import MetricsNavbar from '../ui/MetricsNavbar';
import Narrator from '../ui/Narrator';
import NarratorNotification from '../ui/NarratorNotification';
import BottomBar from '../ui/BottomBar';
import StarBox from '../ui/StarBox';

export default {
  // $emit 'input' on complete
  components: {
    MetricsNavbar,
    Narrator,
    NarratorNotification,
    BottomBar,
    StarBox,
  },
  data() {
    return {
      showNextButton: false,
      showDownloading: false,
      showNarrator: false,
      showNarratorNotification: false,
      selectedTab: 0,
      sortedTopArchetypeNames: ['THE CURIOUS', 'THE EMPATH', 'THE DRIVEN'],
      sortedTopArchetypeValues: [8, 7, 6],
      archetypeIcons: {
        'THE COLLABORATOR': 'Icon_Analyst',
        'THE INSIGHTFUL': 'Icon_Curious',
        'THE DECISIVE': 'Icon_Decider',
        'THE CURIOUS': 'Icon_Perceptive',
        'THE RISK TAKER': 'Icon_Regulator',
        'THE DRIVEN': 'Icon_Driver',
        'THE RESILIENT': 'Icon_Pioneer',
        'THE NAVIGATOR': 'Icon_Navigator',
        'THE INCLUSIVE LEADER': 'Icon_Connector',
        'THE EMPATH': 'Icon_Empathizer',
        'THE COURAGEOUS': 'Icon_Principled',
        'THE STRATEGIST': 'Icon_Strategist',
      },
      archetypeBehaviors: {
        'THE COLLABORATOR': [
          { text: 'You find it easy to collaborate rather than compete with others and frequently seek to do so.', level: 0 },
          { text: 'You are adaptive in your relationships and are able to meet people where they are at and with what they need.', level: 0 },
          { text: 'It’s generally quite easy for you to fit into almost any group of people.', level: 0 },
        ],
        'THE INSIGHTFUL': [
          { text: 'When making decisions, you often see and consider future implications first.', level: 0 },
          { text: 'You typically learn from multiple perspectives and sources on any topic or decision.', level: 0 },
          { text: 'When deriving solutions, you generally attempt to integrate different perspectives into the plan.', level: 0 },
        ],
        'THE DECISIVE': [
          { text: 'You often make decisions quickly and decisively.', level: 0 },
          { text: 'Even when there is limited information, you still feel comfortable stepping up and making a decision.', level: 0 },
          { text: 'You likely find yourself feeling that being able to make any decision is better than settling on no decision whatsoever.', level: 0 },
        ],
        'THE CURIOUS': [
          { text: 'You are often eager to try out new ideas, frameworks, models, and strategies.', level: 0 },
          { text: 'You can be a well-spring of new ideas and come up with new suggestions regularly.', level: 0 },
          { text: 'You’re often on the lookout for new solutions to routine processes even when there isn’t a clear issue.', level: 0 },
        ],
        'THE RISK TAKER': [
          { text: 'You often identify opportunities to take a risk on something new.', level: 0 },
          { text: 'You feel comfortable doing new things even when there may be a risk of failure.', level: 0 },
          { text: 'You are at ease in unfamiliar situations and may even prefer or enjoy doing something where the process isn’t clear.', level: 0 },
        ],
        'THE DRIVEN': [
          { text: 'You generally feel comfortable putting in whatever energy it takes to excel.', level: 0 },
          { text: 'You often thrive with deadlines, which only further drive you to great results.', level: 0 },
          { text: 'Overall, you find it easy to persevere to complete tasks even when distractions or barriers come up.', level: 0 },
        ],
        'THE RESILIENT': [
          { text: 'You often remain calm and collected when under stress.', level: 0 },
          { text: 'You generally are at ease when making tough calls fast even when under pressure.', level: 0 },
          { text: 'When setbacks and adversity arise, you commonly swiftly move past them.', level: 0 },
        ],
        'THE NAVIGATOR': [
          { text: 'You have a belief in the nature of your personal capabilities.', level: 0 },
          { text: 'When opportunities arise, you feel comfortable stepping into them without hesitation.', level: 0 },
          { text: 'When a difficult task or challenge arises, you see it as the opportunity to put yourself forward to solve it.', level: 0 },
        ],
        'THE INCLUSIVE LEADER': [
          { text: 'You inherently see everyone as capable and with something to bring to the table.', level: 0 },
          { text: 'Getting along with almost everyone from virtually any background comes easily to you.', level: 0 },
          { text: 'You give equal respect for the opinions of other people regardless of their social or business standing.', level: 0 },
        ],
        'THE EMPATH': [
          { text: 'You readily and easily pick up on the mood of people around you.', level: 0 },
          { text: 'It is generally easy for you to understand what matters to your co-workers and colleagues.', level: 0 },
          { text: 'You tend to be a good ally to those around you as you are regularly empathetic toward the people around you.', level: 0 },
        ],
        'THE COURAGEOUS': [
          { text: 'Even when others don’t agree, you are comfortable challenging the status quo.', level: 0 },
          { text: 'You often like to contribute your perspective to a discussion before there is a clear consensus so more perspectives can be considered.', level: 0 },
          { text: 'You’re willing to speak out and share a minority opinion whether it is popular or not.', level: 0 },
        ],
        'THE STRATEGIST': [
          { text: 'Frequently your intuition can make creative leaps and connections.', level: 0 },
          { text: 'When faced with complexity, you can quickly cut through it to understand the fundamental systems.', level: 0 },
          { text: 'It is common for you to identify new angles to a situation or problem that others haven’t seen or aren’t seeing.', level: 0 },
        ],
      },
      readMoreLinks: [
        { text: 'YOUR DEVELOPMENT', verb: 'READ', link: 'https://www2.deloitte.com/lu/en/pages/careers/articles/your-development.html' },
        { text: 'YOUR BENEFITS & REWARDS', verb: 'READ', link: 'https://www2.deloitte.com/lu/en/pages/careers/articles/your-benefits-and-rewards.html' },
        { text: 'YOUR LIFE IN LUXEMBOURG', verb: 'READ', link: 'https://www2.deloitte.com/lu/en/pages/careers/articles/your-life-at-luxembourg.html' },
        { text: 'MEET YOUR COLLEAGUES', verb: 'WATCH', link: 'https://www2.deloitte.com/lu/en/pages/careers/articles/meet-your-colleagues.html' },
        { text: 'DISCOVER WHAT WE DO', verb: 'READ', link: 'https://www2.deloitte.com/lu/en/pages/careers/articles/discover-what-we-do.html' },
        { text: '10 REASONS TO JOIN US', verb: 'WATCH', link: 'https://www.youtube.com/watch?v=H3dbycvKKk0' },
      ],
      pdf: false
    };
  },
  computed: {
    ...mapGetters([
      'activeEnvironment',
      'activeClass',
      'characters',
      'tooltip',
      'narrative',
      'lastNarrative',
      'nextNarrative',
      'narrativeNavbarText',
      'narrativeText',
      'group',
      'me',
      'metrics',
      'game',
      'dateActiveGameCompleted',
      'challenge'
    ]),
    username() {
      const {
        firstName = '',
        lastName = '',
        email = ''
      } = this.me || {};
      return firstName || lastName ? `${firstName} ${lastName}` : email;
    },
    date() {
      return (this.game.carryOverMetricsFromActive && this.dateActiveGameCompleted != null)
        ? new Date(this.dateActiveGameCompleted).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        : ((this.challenge && this.challenge.completed)
          ? new Date(this.challenge.completed).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
          : ((this.group && this.group.updated)
            ? new Date(this.group.updated).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
            : new Date(Date.now()).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })));
    },
    reportFileName() {
      return (this.activeClass != null && this.activeClass.studentReportFileName != null)
        ? this.activeClass.studentReportFileName
        : (this.activeEnvironment.reportTitle || this.game.reportTitle || 'Global Leadership Framework Overview');
    },
    selectedArchetype() {
      return (this.sortedTopArchetypeNames
        && this.sortedTopArchetypeNames.length > this.selectedTab)
        ? this.sortedTopArchetypeNames[this.selectedTab]
        : 'THE CURIOUS';
    },
    selectedArchetypeSummary() {
      if (this.selectedArchetype === 'THE COLLABORATOR') {
        return 'You have a natural gift to work collaboratively with others and bring people into collaboration with you.';
      }
      if (this.selectedArchetype === 'THE INSIGHTFUL') {
        return 'You have an ability to rapidly synthesize context and develop insight in almost any situation.';
      }
      if (this.selectedArchetype === 'THE DECISIVE') {
        return 'You’re at ease when it comes to making decisions in an urgent or emergency situation.';
      }
      if (this.selectedArchetype === 'THE CURIOUS') {
        return 'You have a passion for innovation and change that can influence the environment around you.';
      }
      if (this.selectedArchetype === 'THE RISK TAKER') {
        return 'You are often bold in the face of ambiguity, seeing change and uncertainty as opportunities to take action.';
      }
      if (this.selectedArchetype === 'THE DRIVEN') {
        return 'You have an innate, persistent desire to excel and apply yourself to achievement.';
      }
      if (this.selectedArchetype === 'THE RESILIENT') {
        return 'When times get tough, you generally get tougher showing strong mental resilience in difficult times.';
      }
      if (this.selectedArchetype === 'THE NAVIGATOR') {
        return 'You have confidence in your ability to take on challenging tasks and navigate your destiny.';
      }
      if (this.selectedArchetype === 'THE INCLUSIVE LEADER') {
        return 'You have a natural optimism about the capabilities of others, and easily see opportunities to leverage these diverse strengths.';
      }
      if (this.selectedArchetype === 'THE EMPATH') {
        return 'You have an ability to understand people and what they are feeling and are able to be sensitive to those emotions in decision-making.';
      }
      if (this.selectedArchetype === 'THE COURAGEOUS') {
        return 'You can be fearless in the face of adversity or traditional hierarchy and are willing to speak up and speak out.';
      }
      if (this.selectedArchetype === 'THE STRATEGIST') {
        return 'You rapidly grasp complex situations when attempting to identify the best solutions.';
      }
      return 'You rapidly grasp complex situations when attempting to identify the best solutions.';
    },
    selectedArchetypeIcon() {
      return this.archetypeIcons[this.selectedArchetype];
    },
    selectedArchetypeBehaviors() {
      return this.archetypeBehaviors[this.selectedArchetype];
    },
    sortedTopArchetypeIcons() {
      return this.sortedTopArchetypeNames.map(n => this.archetypeIcons[n]);
    },
  },
  methods: {
    ...mapActions([
      'stopSounds',
      'getMyGroup'
    ]),
    async show() {
      this.showNarrator = true;
      await Vue.nextTick();
      this.stopSounds();
      await new Promise(resolve => setTimeout(resolve, 1250));
      this.showNarratorNotification = true;
      await new Promise(resolve => setTimeout(resolve, 1250));
      await Vue.nextTick();
    },
    async reset() {
      await Vue.nextTick();
      await this.show();
    },
    navigate() {
      this.$refs.bottomBar.navigate();
    },
    async onComplete() {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        window.dispatchEvent(new Event('click'));
        return;
      }
      this.$emit('input');
    },
    async loadPageData() {
      try {
        const { _id: groupId } = this.group;
        await this.getMyGroup({ groupId });
      } catch (err) {
        if (await this.$router.handleGameError(err)) return;
        this.$router.showGameErrorCritical();
      }
    },
    setStats() {
      console.log(this.group);
      // TODO: the following order-dependent coupling is not ideal, but it currently seems
      //       to be the best way to do this given the way the data is currently organized.
      //       An ID-based coupling or a name-based coupling seems similarly dicey,
      //       and they'd just be costlier to compute.

      const archetypeScores = [];
      for (let i = 3; i < this.group.metrics.length; i += 4) {
        archetypeScores.push({ index: i - 3, value: this.group.metrics[i].value });
      }
      archetypeScores.sort((a, b) => b.value - a.value);

      console.log(archetypeScores);

      if (archetypeScores.length > 2) {
        this.sortedTopArchetypeNames.length = 0;
        this.sortedTopArchetypeNames.push(this.archetypeIndexMap(archetypeScores[0].index));
        this.sortedTopArchetypeNames.push(this.archetypeIndexMap(archetypeScores[1].index));
        this.sortedTopArchetypeNames.push(this.archetypeIndexMap(archetypeScores[2].index));
        this.sortedTopArchetypeValues.length = 0;
        this.sortedTopArchetypeValues.push(archetypeScores[0].value);
        this.sortedTopArchetypeValues.push(archetypeScores[1].value);
        this.sortedTopArchetypeValues.push(archetypeScores[2].value);
        this.sortedTopArchetypeNames.forEach((n, i) => {
          const behaviors = this.archetypeBehaviors[n];
          behaviors[0].level = this.group.metrics[archetypeScores[i].index].value;
          behaviors[1].level = this.group.metrics[archetypeScores[i].index + 1].value;
          behaviors[2].level = this.group.metrics[archetypeScores[i].index + 2].value;
        });
      }
    },
    archetypeIndexMap(metricIndex) {
      switch (metricIndex) {
        case 44: return 'THE STRATEGIST';
        case 40: return 'THE INSIGHTFUL';
        case 36: return 'THE DECISIVE';
        case 32: return 'THE INCLUSIVE LEADER';
        case 28: return 'THE EMPATH';
        case 24: return 'THE COLLABORATOR';
        case 20: return 'THE NAVIGATOR';
        case 16: return 'THE CURIOUS';
        case 12: return 'THE RISK TAKER';
        case 8: return 'THE DRIVEN';
        case 4: return 'THE COURAGEOUS';
        case 0: return 'THE RESILIENT';
        default: return '';
      }
    },
    onBeforePrint(mql) {
      if (mql instanceof Event) {
        this.pdf = true;
        setTimeout(() => {
          this.pdf = false;
        }, 250);
      } else if (mql.matches) {
        this.pdf = true;
        setTimeout(() => {
          this.pdf = false;
        }, 250);
      }
    },
    onClickTab(index) {
      this.selectedTab = index;
    },
    iOS() {
      const platform = navigator.platform || navigator.userAgentData.platform;
      return [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod'
      ].includes(platform)
      // iPad on iOS 13 detection
      || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
    },
    isSafari() {
      return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    },
    async onClickDownload() {
      if (this.showDownloading) return;
      this.showDownloading = true;
      // PDF download works in all browsers except pre-chromium Edge
      // (fails at domtoimage.toPng, and no replacement seems viable, see above)
      // fix for Edge (for now): make the page printable, and open the print dialog automatically
      // (in response to domtoimage.toPng or any other part of the autodownload throwing any error)
      // The user can print to PDF or actually print. Bonus is you can print in other browsers too.
      // Edge requires the user to manually set the Scale in the print dialog to 50% for it to fit.
      // Note that this is generally no longer a problem with the new chromium-based Edge.
      this.pdf = true;
      try {
        this.selectedTab = 0;
        await Vue.nextTick();
        let dataUrl = await domtoimage.toPng(this.$refs.report, { cacheBust: true });
        // redundant calls required to work on Safari... (https://github.com/tsayen/dom-to-image/issues/343)
        const brokenBecauseSafari = this.iOS() || this.isSafari();
        if (brokenBecauseSafari) {
          dataUrl = await domtoimage.toPng(this.$refs.report);
          dataUrl = await domtoimage.toPng(this.$refs.report);
        }
        const doc = new JsPDF('p', 'pt', 'a4');
        const isFooterPresent = !!(this.activeEnvironment.reportFooter);
        doc.addImage(dataUrl, 'PNG', 28, 50, 540, 605);
        // NOTE: all these numbers are empirically determined; could break if the report is changed
        // TODO: add a utility to get DOM transform of element and convert to PDF transform
        this.readMoreLinks.forEach((link, i) => {
          doc.link(68 + (77 * i), (isFooterPresent ? 618 : 630), 70, 20, { url: link.link });
        });
        this.selectedTab = 1;
        await Vue.nextTick();
        doc.addPage();
        dataUrl = await domtoimage.toPng(this.$refs.report, { cacheBust: true });
        if (brokenBecauseSafari) {
          dataUrl = await domtoimage.toPng(this.$refs.report);
        }
        doc.addImage(dataUrl, 'PNG', 28, 50, 540, 605);
        this.readMoreLinks.forEach((link, i) => {
          doc.link(68 + (77 * i), (isFooterPresent ? 618 : 630), 70, 20, { url: link.link });
        });
        this.selectedTab = 2;
        await Vue.nextTick();
        doc.addPage();
        dataUrl = await domtoimage.toPng(this.$refs.report, { cacheBust: true });
        if (brokenBecauseSafari) {
          dataUrl = await domtoimage.toPng(this.$refs.report);
        }
        doc.addImage(dataUrl, 'PNG', 28, 50, 540, 605);
        this.readMoreLinks.forEach((link, i) => {
          doc.link(68 + (77 * i), (isFooterPresent ? 618 : 630), 70, 20, { url: link.link });
        });
        this.selectedTab = 0;

        // TODO: if no autodownload, no soft-lock (explained below), so soft-lock fix commented out
        doc.save(`${this.reportFileName}.pdf`);
        // Before we download the PDF, we have to consider the possibility that the
        // user's settings are set to view the PDF in the browser first when downloading a PDF
        // (instead of actually downloading it). This can be true in many modern browsers.
        // This is a problem because if it opens in the same page, it destroys the Vue app state.
        // This ends up soft-locking the player such that they can't get past the report page.
        // This problem came to my attention when a Firefox update changed the PDF download behavior
        // to default to viewing it in browser (on the same page) when you initiate a download.
        // To fix this while minimally impacting anyone whose browser actually just downloads stuff
        // when a download is initiated, instead first open a new tab, then artificially initiate
        // the download with a bogus DOM hyperlink element that targets the bloburl output by JsPDF,
        // which either downloads the file or loads the blob viewer. If it doesn't load the
        // blob viewer, we autoclose the tab, leaving you back on the report page. If it does load
        // the blob viewer, the user is left in the blob viewer, where they can either:
        // - close it, returning to the original report tab without downloading, where they can
        //   hit print or refresh to save off the report another way, or continue on with the game.
        // - download/print it (which presents a system dialog, and does carry over the expected
        //   default name) (and then close it to return back to the game).
        // eslint-disable-next-line max-len
        // const win = window.open(`${window.location.origin}/downloadplayerreport/${encodeURIComponent(this.reportFileName)}/${encodeURIComponent(doc.output('bloburl', { filename: `${this.reportFileName}.pdf` }))}`, '_blank');
        // if (win == null) {
        //   doc.save(`${this.reportFileName}.pdf`);
        // }

        this.pdf = false;
        this.showDownloading = false;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to download report PDF!', error);
        try {
          this.pdf = true;
          this.selectedTab = 0;
          await Vue.nextTick();
          window.print();
          this.pdf = false;
          this.showDownloading = false;
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Failed to open print dialog!', e);
          this.pdf = false;
          this.showDownloading = false;
          this.selectedTab = 0;
        }
      }
    },
  },
  watch: {
    async narrative(newValue) {
      if (newValue) await this.reset();
    },
  },
  async mounted() {
    this.setStats();

    // polyfill for safari but works for others as well
    const mediaQueryList = (window.matchMedia != null) ? window.matchMedia('print') : null;
    if (mediaQueryList != null && mediaQueryList.addListener != null) {
      mediaQueryList.removeListener(this.onBeforePrint);
      mediaQueryList.addListener(this.onBeforePrint);
    } else {
      // what you'd normally use, if you weren't needing to support Safari:
      window.removeEventListener('beforeprint', this.onBeforePrint);
      window.addEventListener('beforeprint', this.onBeforePrint);
    }

    await this.show();

    await new Promise((resolve, reject) => {
      if (this.$refs.logo.complete) { // wait for logo to load
        resolve();
      } else {
        this.$refs.logo.addEventListener('load', resolve);
        this.$refs.logo.addEventListener('error', reject);
      }
    });

    // uncomment to enable autodownload (but re-add soft-lock fix above if you do)
    // await this.onClickDownload();

    this.showNextButton = true;
  },
};
</script>

<style lang="scss" scoped>
@media print {
  body, body * {
    visibility: hidden !important;
    overflow: visible !important;
  }
  #report-container, #report-container * {
    visibility: visible !important;
    overflow: visible !important;
  }
  #report-container {
    left: 8.75% !important;
    top: 0 !important;
    width: 82.5% !important;
    overflow: visible !important;
    z-index: 10 !important;

    // this makes background graphics show up without needing to check the box
    color-adjust: exact !important;
    print-color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
  }
}

.no-wrap {
  white-space: nowrap;
}

.download-button {
  position: absolute;
  bottom: 210px;
  right: 0;
  width: 350px;
  font-size: 28px;
  line-height: 55px;
  margin-right: 45px;
  z-index: 100 !important;
  transition: opacity 0.5s ease-in-out;
}

hr {
  border-top: 1px solid #e9ecef;
}
.report {
  background-color: white;
  color: black;
}
.report.pdf {
  border: solid;
  border-width: 3.5px;
  border-color: var(--primaryColor);
}
.report-container {
  position: absolute;
  width: 1150px;
  height: 850px;
  top: 20px;
  left: 30px;
  z-index: 1;
  overflow: auto;
}

.report-header {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  height: 60px;
  padding: 0 50px;
  background-color: black;
  color: lightgrey;
}
.logo {
  object-fit: contain;
  height: 90%;
  max-width: 40%;
  z-index: 10;
}
.report-title {
  font-size: 1.96em;
  white-space: pre-wrap;
  text-align: right;
  margin: auto 0;
  max-width: 60%;
  max-height: 100%;
  line-height: 100%;
}

.middle-banner {
  background-image: url('/static/reports/deloittemiddlebanner.png');
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  color: white;
}

.links-banner {
  background-image: url('/static/reports/deloittelinksbar.png');
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  color: white;
}

.archetype-tabs {
  width: 100%;
  height: 80px;
  background-color: rgba(134,188,37,1);
  position: relative;
  margin: 0;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-evenly;
  align-items: center;
  padding: 0 130px;
}

.archetype-tab {
  z-index: 2;
  background-color: rgba(134,188,37,1);
  border: 1px solid white;
  padding: 6px 0;
  width: calc(33% - 12px);
  font-size: 21px;
  color: white;
  text-align: center;
  white-space: nowrap;
  -webkit-appearance: none;
}

.archetype-tab.selected {
  background-color: rgb(116, 156, 42);
  box-shadow: 0 0 3.5px black,
              0 0 3.5px black,
              0 0 3.5px black,
              0 0 3.5px black,
              0 0 3.5px black,
              0 0 3.5px black,
              0 0 3.5px black;
}

.archetype-header {
  width: 100%;
  height: 80px;
  padding: 0;
  font-size: 30px;
  color: black;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  position: relative;
}

.behavior-bullet {
  width: 60px;
  height: 60px;
  background-image: url('/static/reports/bulletbggreen.png');
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
}

.behavior-bullet-icon {
  width: 59%;
  height: 59%;
  object-fit: contain;
  margin: 0 0 5px 6px;
}

.selected-icon {
  width: 100%;
  height: 100%;
  z-index: 10;
  object-fit: contain;
}

.slideInDown {
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.19, 1, 0.22, 1); // out expo
}
.fadeOutUp {
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19); // in cubic
}
.narrator-notification {
  bottom: 205px;
}

.download-message {
  position: absolute;
  top: 50%;
  left: 25%;
  z-index: 1000;
  border-radius: 15px;
  border: 3px solid black;
  background-color: var(--primaryColor);
  color: white;
  font-size: 30pt;
  padding: 20px;
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/components/game/narrative/NarrativeDeloitteReport.vue